if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let mutationAccepted = false;

        return fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const body = await response.json();
            if (!response.ok) body.status = response.status;
            return body;
          })
          .then(async (response) => {
            if (response.status) {
              const errorMessage = response.description || response.message ||
                (typeof response.errors === 'string' ? response.errors : 'Unable to add this item. Please try again.');
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(errorMessage);
              const responseStatus = Number(response.status);
              this.dispatchCartErrorEvent(errorMessage, responseStatus === 429 || responseStatus >= 500 ? 'SERVICE_UNAVAILABLE' : 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage));

              // Shopify also uses 422 for quantity and validation errors. Only its
              // explicit sold-out description makes a catalog card unavailable.
              // Unknown/localized errors keep the server message and remain retryable.
              const isSoldOut = Number(response.status) === 422 &&
                typeof response.description === 'string' &&
                /^(?:The product [\s\S]+ is already sold out\.?|This item is sold out\.?)$/i.test(response.description.trim());
              if (Number(response.status) === 422 && !isSoldOut) {
                // An over-stock 422 may still add the available quantity. Read the
                // resulting cart; never replay the mutation to repair its markup.
                try {
                  await this.reconcileRejectedCart(errorMessage);
                } catch (error) {
                  console.error(error);
                  this.handleErrorMessage(`${errorMessage} Open your cart to check its current contents.`);
                }
              }
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!isSoldOut || !soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            mutationAccepted = true;
            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const renderCart = () => {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
                // The outer empty state belongs to the rendered cart, never request cleanup.
                this.cart.classList.remove('is-empty');
              });
            };
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              return new Promise((resolve, reject) => {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      try { renderCart(); resolve(); } catch (error) { reject(error); }
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              });
            } else {
              renderCart();
            }
          })
          .catch((e) => {
            console.error(e);
            const errorMessage = mutationAccepted
              ? 'The item was added, but your cart could not be displayed. Open your cart to continue.'
              : 'Unable to add this item. Please try again.';
            this.handleErrorMessage(errorMessage);
            this.dispatchCartErrorEvent(errorMessage, 'SERVICE_UNAVAILABLE');
            if (!mutationAccepted) linesUpdateDeferred?.reject(e);
            else if (!this.isConnected) window.location.assign(window.routes.cart_url);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async reconcileRejectedCart(errorMessage) {
        const cartResponse = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Unable to read the current cart.');
        const cartState = await cartResponse.json();
        if (!Number.isInteger(cartState.item_count) || !Array.isArray(cartState.items)) {
          throw new Error('Invalid cart response.');
        }

        const drawer = document.querySelector('cart-drawer');
        const sectionIds = drawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
        const sectionsResponse = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(sectionIds.join(','))}`, {
          credentials: 'same-origin', cache: 'no-store',
        });
        if (!sectionsResponse.ok) throw new Error('Unable to refresh the current cart.');
        const sections = await sectionsResponse.json();
        const parser = new DOMParser();
        // Validate every replacement before changing either drawer or bubble.
        const replacements = sectionIds.map((id) => {
          if (typeof sections[id] !== 'string') throw new Error(`Missing cart section: ${id}`);
          const parsed = parser.parseFromString(sections[id], 'text/html');
          const source = parsed.querySelector(id === 'cart-drawer' ? '#CartDrawer' : '.shopify-section');
          const target = document.getElementById(id === 'cart-drawer' ? 'CartDrawer' : id);
          if (!source || !target) throw new Error(`Unable to render cart section: ${id}`);
          return { source, target };
        });
        replacements.forEach(({ source, target }) => { target.innerHTML = source.innerHTML; });
        if (drawer) {
          drawer.classList.toggle('is-empty', cartState.item_count === 0);
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        }
        // Subscribers may refresh drawer items again, so restore the server error
        // only after those reads settle rather than letting their markup erase it.
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form-recovery', cartData: cartState });
        if (drawer) {
          const drawerError = drawer.querySelector('#CartDrawer-CartErrors');
          if (drawerError) drawerError.textContent = errorMessage;
          // Keep an attached product/quick-add error visible. If its modal vanished
          // while reading, show the real nonempty cart with the same error instead.
          if (!this.isConnected && cartState.item_count > 0) drawer.open();
        }
        return cartState;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
